// What a clip's source plays and shows: its signed URL, a poster and a name. A project's saved
// sources come in one read (spec editor/project-media.md); a source that read does not list, a
// clip added since, is read on its own (the asset, the job whose output it is, or the actor), so
// it plays even when its file is not on the media bin's first page. The reads share the query
// cache with the rest of the app.
import { replaceEqualDeep, useQueries, useQuery } from "@tanstack/react-query";

import { getGetAssetQueryOptions } from "../../api/generated/endpoints/assets/assets";
import { getGetEditProjectMediaQueryOptions } from "../../api/generated/endpoints/editor/editor";
import {
  getGetJobQueryOptions,
  getGetTeamActorQueryOptions,
} from "../../api/generated/endpoints/generation/generation";
import type { ActorSource, AiActor, Asset, EditProjectMedia, Job, ProxyStatus } from "../../api/generated/model";
import { freshUrls, keptUrl, signedExpiry, soonestExpiry } from "../../api/signed-urls";
import { PENDING_COPY_POLL_MS } from "../../platform/media/library-pages";
import { useSourceLengths } from "./length";
import type { ClipSource } from "./timeline";

export interface SourceMedia {
  kind: "video" | "image" | "audio";
  /** The playable file; `null` while it is not ready or its URL is not signed. */
  url: string | null;
  poster: string | null;
  name: string;
  /** The source's length, when the server knows it. */
  durationMs: number | null;
  /** The picture's own size, when the server knows it (a fresh upload's waits for its probe). */
  width: number | null;
  height: number | null;
  /** A library video's 540p copy, which the preview plays (spec editor/proxy-playback.md): its URL
   * once made, and where it stands; both null for a picture, a sound, an output or an actor. */
  proxy: string | null;
  proxyStatus: ProxyStatus | null;
}

/** One key per source, for maps and React keys. */
export function sourceKey(source: ClipSource): string {
  if (source.kind === "asset") return `asset:${source.assetId}`;
  if (source.kind === "actor") return `actor:${source.actorId}`;
  return `output:${source.jobId}:${source.index}`;
}

/** The key of a source as the server names it: a global actor's is an actor's, as wire.ts reads it. */
function wireSourceKey(source: ActorSource): string {
  if (source.kind === "asset") return sourceKey({ kind: "asset", assetId: source.asset_id });
  if (source.kind === "job_output") {
    return sourceKey({ kind: "output", jobId: source.job_id, index: source.output_index });
  }
  return sourceKey({ kind: "actor", actorId: source.actor_id });
}

/** A generation's name past this many characters is cut, with an ellipsis. */
const OUTPUT_NAME_MAX = 60;

function shortName(name: string): string {
  return name.length > OUTPUT_NAME_MAX ? `${name.slice(0, OUTPUT_NAME_MAX - 1)}…` : name;
}

/** One of the team's actors as a picture: its portrait (live's media panel's Avatar, #244). */
export function actorMedia(actor: AiActor): SourceMedia {
  return {
    kind: "image",
    url: actor.image_url,
    poster: actor.image_url,
    name: actor.name,
    durationMs: null,
    width: actor.width,
    height: actor.height,
    proxy: null,
    proxyStatus: null,
  };
}

export function assetMedia(asset: Asset): SourceMedia {
  // A picture is its own poster.
  const picture = asset.kind === "image" ? asset.urls.original : null;
  return {
    kind: asset.kind,
    url: asset.urls.original,
    poster: asset.urls.poster ?? asset.urls.thumb ?? picture,
    name: asset.name,
    durationMs: asset.duration_ms,
    width: asset.width,
    height: asset.height,
    proxy: asset.urls.proxy ?? null,
    proxyStatus: asset.proxy_status,
  };
}

/** A job's delivered output (a video, a picture or a sound), or `undefined` when it is not one. */
export function outputMedia(job: Job, index: number): SourceMedia | undefined {
  const output = job.outputs.find((each) => each.index === index);
  const media = /^(video|image|audio)\//.test(output?.mime ?? "");
  if (output === undefined || output.status !== "delivered" || !media) return undefined;
  const prompt = job.params?.prompt?.trim() ?? "";
  const name = prompt === "" ? `Generation ${job.id.slice(0, 8)}` : prompt;
  const kind = output.mime?.startsWith("image/") ? "image" : output.mime?.startsWith("audio/") ? "audio" : "video";
  // A video's poster once made (issue 828), else its thumb; a picture's thumb, else itself.
  const picture = kind === "image" ? output.url : null;
  return {
    kind,
    url: output.url,
    poster: output.poster_url ?? output.thumb_url ?? picture,
    name: shortName(name),
    durationMs: output.duration_ms,
    width: output.width,
    height: output.height,
    proxy: null,
    proxyStatus: null,
  };
}

/** What the project's media read says of a source: its media, or null for one that is gone. */
function listedMedia(each: EditProjectMedia["sources"][number]): SourceMedia | null {
  if (each.status === "gone") return null;
  return {
    kind: each.kind,
    url: each.url,
    poster: each.poster,
    // The server names a generation by its whole prompt; the editor cuts it, as `outputMedia`.
    name: each.source.kind === "job_output" ? shortName(each.name) : each.name,
    durationMs: each.duration_ms,
    width: each.width,
    height: each.height,
    proxy: each.proxy,
    proxyStatus: each.proxy_status,
  };
}

function listedOf(media: EditProjectMedia): ReadonlyMap<string, SourceMedia | null> {
  return new Map(media.sources.map((each) => [wireSourceKey(each.source), listedMedia(each)]));
}

/** The project's media is read again before its signed URLs expire (G5). */
const listedUrls = freshUrls<EditProjectMedia>((media) => media.urls_expire_at);

/** Whether a project's media read names a copy still being made. */
function copyPending(media: EditProjectMedia | undefined): boolean {
  return media?.sources.some((each) => each.status === "ready" && each.proxy_status === "pending") ?? false;
}

/**
 * A read of the project's media as shown: each source's URLs kept as they were while they name
 * the same file and stay good (`keptUrl`), so a read again (a pending copy's, or a refresh before
 * a 15-minute step) reloads no clip; a new file, a copy made among them, comes at once. Its
 * expiry is the soonest of the URLs it keeps when that comes before the read's own, so the next
 * read is due before a kept URL stops working (review of #1144).
 */
export function keptProjectMedia(
  shown: EditProjectMedia | undefined,
  read: EditProjectMedia,
  now: number = Date.now(),
): EditProjectMedia {
  if (shown === undefined) return read;
  const before = new Map(shown.sources.map((each) => [wireSourceKey(each.source), each]));
  const sources = read.sources.map((each) => {
    const was = before.get(wireSourceKey(each.source));
    if (each.status !== "ready" || was?.status !== "ready") return each;
    return {
      ...each,
      url: keptUrl(was.url, each.url, now) ?? each.url,
      poster: keptUrl(was.poster, each.poster, now),
      proxy: keptUrl(was.proxy, each.proxy, now),
    };
  });
  const keptUntil = soonestExpiry(
    sources.flatMap((each) => (each.status === "ready" ? [each.url, each.poster, each.proxy].map(signedExpiry) : [])),
  );
  const sooner =
    keptUntil !== null && (read.urls_expire_at === null || Date.parse(keptUntil) < Date.parse(read.urls_expire_at));
  // What did not change stays the same object, as TanStack's own sharing keeps it.
  return replaceEqualDeep(shown, { ...read, sources, urls_expire_at: sooner ? keptUntil : read.urls_expire_at });
}

/**
 * The project's media read's `structuralSharing`. TanStack shares through it both the read and
 * what `select` makes of it, the Map `listedOf` builds (review of #1144): the read keeps its URLs
 * as shown (`keptProjectMedia`), and the Map, made anew from that kept read, is taken as it
 * comes, as TanStack's own sharing takes a Map.
 */
export function sharedProjectMedia(shown: unknown, read: unknown): unknown {
  if (read instanceof Map || shown instanceof Map) return read;
  return keptProjectMedia(shown as EditProjectMedia | undefined, read as EditProjectMedia);
}

/**
 * Every source of the saved project with its media, by `sourceKey`, in one read (spec
 * editor/project-media.md); a source that is gone maps to null. The editor's screen starts it
 * beside the project's own read, so the two go out together. It is read again every
 * `PENDING_COPY_POLL_MS` while a video's copy is being made, its URLs kept as shown.
 */
export function useProjectMedia(teamId: string, brandId: string, projectId: string) {
  return useQuery({
    ...getGetEditProjectMediaQueryOptions(teamId, brandId, projectId),
    select: listedOf,
    refetchInterval: (query) =>
      copyPending(query.state.data) ? PENDING_COPY_POLL_MS : listedUrls.refetchInterval(query),
    refetchOnWindowFocus: listedUrls.refetchOnWindowFocus,
    structuralSharing: sharedProjectMedia,
  });
}

function distinct(sources: readonly ClipSource[]): ClipSource[] {
  const seen = new Map<string, ClipSource>();
  for (const source of sources) seen.set(sourceKey(source), source);
  return [...seen.values()];
}

/**
 * The media of every source, by `sourceKey`; a source still loading or gone is absent. The
 * project's media read answers for its saved sources; any other source is read on its own once
 * that read is in, and every one while it is failing, a failed refresh included. A source whose
 * length the server does not know yet gets the length read from its file (length.ts).
 */
export function useSourceMedia(
  teamId: string,
  brandId: string,
  projectId: string,
  sources: readonly ClipSource[],
): ReadonlyMap<string, SourceMedia> {
  const project = useProjectMedia(teamId, brandId, projectId);
  // A failed read, a refresh of its URLs among them, keeps its last answer in the cache, whose URLs
  // may have stopped working: every source is then read on its own, until a read lands again.
  const listed = project.isError ? undefined : project.data;
  const answered = project.isSuccess || project.isError;
  const unique = distinct(sources);
  const own = answered ? unique.filter((source) => listed?.has(sourceKey(source)) !== true) : [];
  const found = useQueries({
    queries: own.map((source) => {
      if (source.kind === "asset") {
        return {
          ...getGetAssetQueryOptions(teamId, brandId, source.assetId),
          select: (asset: Asset): SourceMedia | undefined => assetMedia(asset),
          // A clip added since the project's read: its copy's arrival too (proxy-playback.md).
          refetchInterval: (query: { state: { data: Asset | undefined } }) =>
            query.state.data?.proxy_status === "pending" ? PENDING_COPY_POLL_MS : false,
        };
      }
      if (source.kind === "actor") {
        return {
          ...getGetTeamActorQueryOptions(teamId, source.actorId),
          select: (actor: AiActor): SourceMedia | undefined => actorMedia(actor),
        };
      }
      return {
        ...getGetJobQueryOptions(teamId, source.jobId),
        select: (job: Job): SourceMedia | undefined => outputMedia(job, source.index),
      };
    }),
    combine: (results) => {
      const media = new Map<string, SourceMedia>();
      results.forEach((result, at) => {
        if (result.data !== undefined) media.set(sourceKey(own[at]!), result.data);
      });
      return media;
    },
  });
  const files = new Map<string, SourceMedia>();
  for (const source of unique) {
    const key = sourceKey(source);
    const each = listed?.has(key) === true ? listed.get(key) : found.get(key);
    if (each !== undefined && each !== null) files.set(key, each);
  }
  const lengths = useSourceLengths(
    unique.map((source) => {
      const key = sourceKey(source);
      const media = files.get(key);
      // A still has no length to read.
      const url = media?.kind === "image" ? null : (media?.url ?? null);
      return { key, url, knownMs: media?.durationMs ?? null };
    }),
  );
  const media = new Map<string, SourceMedia>();
  for (const [key, each] of files) {
    const length = lengths.get(key);
    media.set(key, { ...each, durationMs: length?.state === "known" ? length.ms : null });
  }
  return media;
}
